import { reactive, computed, unref } from "vue";

/**
 * Composable for managing grouped permission checkboxes.
 * All permissions are stored in a single `form.permissions` array.
 *
 * @param {import('vue').Ref<Object>|import('vue').ComputedRef<Object>} formRef
 *   — reactive ref to the form object (use `toRef(props, 'form')`)
 */
export function usePermissionGroups(formRef) {
  const expandedGroups = reactive({});

  const permissions = computed({
    get() {
      const form = unref(formRef);
      return Array.isArray(form?.permissions) ? form.permissions : [];
    },
    set(val) {
      const form = unref(formRef);
      if (form) form.permissions = val;
    },
  });

  function isGroupChecked(items) {
    if (!Array.isArray(items) || items.length === 0) return false;
    return items.every((i) => permissions.value.includes(i.code));
  }

  function isGroupIndeterminate(items) {
    if (!Array.isArray(items) || items.length === 0) return false;
    const count = items.filter((i) =>
      permissions.value.includes(i.code)
    ).length;
    return count > 0 && count < items.length;
  }

  function toggleGroup(items) {
    if (isGroupChecked(items)) {
      const codes = new Set(items.map((i) => i.code));
      permissions.value = permissions.value.filter((p) => !codes.has(p));
    } else {
      const missing = items
        .filter((i) => !permissions.value.includes(i.code))
        .map((i) => i.code);
      permissions.value = [...permissions.value, ...missing];
    }
  }

  function togglePermission(code) {
    if (permissions.value.includes(code)) {
      permissions.value = permissions.value.filter((p) => p !== code);
    } else {
      permissions.value = [...permissions.value, code];
    }
  }

  function isPermissionChecked(code) {
    return permissions.value.includes(code);
  }

  function toggleExpand(key) {
    expandedGroups[key] = !expandedGroups[key];
  }

  function initExpandedGroups(groupedPermissions) {
    Object.keys(groupedPermissions).forEach((key) => {
      expandedGroups[key] = isGroupChecked(groupedPermissions[key]);
    });
  }

  return {
    expandedGroups,
    permissions,
    isGroupChecked,
    isGroupIndeterminate,
    toggleGroup,
    togglePermission,
    isPermissionChecked,
    toggleExpand,
    initExpandedGroups,
  };
}
